import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Folder, ArrowRight, Sparkles as SparklesIcon, Shield, Zap } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { snappySpring } from '../lib/motion';
import { Spotlight } from '../components/aceternity/Spotlight';
import { Meteors } from '../components/aceternity/Meteors';
import { Sparkles } from '../components/aceternity/Sparkles';
import { GlobeBackground } from '../components/aceternity/GlobeBackground';
import { ShootingStars } from '../components/aceternity/ShootingStars';
import { GridDotBackground } from '../components/aceternity/GridDotBackground';
import { FlipWords } from '../components/aceternity/FlipWords';

export function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const performLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('登录成功');
      navigate('/files');
    } catch (err: any) {
      setError(err.message || '登录失败，请重试');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await performLogin(); } catch { /* handled */ }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Aceternity 背景效果 */}
      <GridDotBackground variant="grid" />
      <Spotlight />
      <Meteors number={16} />
      <Sparkles count={30} className="z-0" />
      <ShootingStars count={8} />
      {/* Aceternity GitHub Globe 地球背景 */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <GlobeBackground globeConfig={{ autoRotateSpeed: 0.6 }} />
      </div>
      <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-bg/20 via-transparent to-bg/60" />
      {/* 装饰光斑 */}
      <div className="pointer-events-none absolute top-10 left-10 w-72 h-72 rounded-full bg-primary/30 blur-[120px] animate-float" />
      <div className="pointer-events-none absolute bottom-10 right-10 w-72 h-72 rounded-full bg-accent-violet/25 blur-[120px] animate-float" style={{ animationDelay: '1s' }} />
      <div className="pointer-events-none absolute top-1/2 right-1/4 w-64 h-64 rounded-full bg-accent-cyan/20 blur-[110px] animate-float" style={{ animationDelay: '2s' }} />

      {/* 悬浮装饰标识 */}
      <div className="pointer-events-none absolute top-[14%] left-[12%] hidden lg:flex items-center gap-1.5 text-text-muted/40 text-xs font-mono rotate-[-8deg] animate-float">
        <Zap className="w-3.5 h-3.5" /> FAST · EDGE
      </div>
      <div className="pointer-events-none absolute bottom-[16%] right-[12%] hidden lg:flex items-center gap-1.5 text-text-muted/40 text-xs font-mono rotate-[6deg] animate-float" style={{ animationDelay: '1.4s' }}>
        <Shield className="w-3.5 h-3.5" /> PRIVATE · SECURE
      </div>

        <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <motion.span
            initial={{ scale: 0.5, opacity: 0, rotate: -30 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 240, damping: 16, delay: 0.05 }}
            className="relative inline-flex items-center justify-center w-20 h-20 rounded-3xl brand-gradient glow-primary mb-5"
          >
            <Folder className="w-9 h-9 text-white" />
            <motion.span animate={{ rotate: 360 }} transition={{ duration: 12, repeat: Infinity, ease: 'linear' }} className="absolute inset-0">
              <SparklesIcon className="w-5 h-5 text-white absolute -top-1 left-1/2 -translate-x-1/2" />
            </motion.span>
          </motion.span>
          <h1 className="text-4xl font-black neon-text tracking-tight">红卫兵网盘</h1>
          <p className="text-text-muted mt-3 text-sm tracking-[0.15em] font-mono">
            <FlipWords words={['私有云存储', '多用户协作', '在线预览', 'WebDAV 挂载']} className="text-primary font-semibold" />
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 26, delay: 0.1 }}
          onSubmit={handleSubmit}
          className="neon-border soft-shadow"
        >
          <div className="p-6 sm:p-7 dot-grid">
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -8 }} animate={{ opacity: 1, height: 'auto', y: 0 }} exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2 p-3 mb-4 bg-danger/10 border border-danger/30 rounded-xl text-danger text-sm overflow-hidden"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mb-4">
              <label htmlFor="email" className="block text-xs font-bold text-text-muted mb-1.5 tracking-wider">邮箱 EMAIL</label>
              <div className="relative group">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted transition-colors group-focus-within:text-primary" />
                <input
                  id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-bg/70 border border-border rounded-xl text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/60 transition-all"
                  placeholder="你的邮箱" required autoComplete="email" disabled={loading}
                />
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="password" className="block text-xs font-bold text-text-muted mb-1.5 tracking-wider">密码 PASSWORD</label>
              <div className="relative group">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted transition-colors group-focus-within:text-primary" />
                <input
                  id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-bg/70 border border-border rounded-xl text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/60 transition-all"
                  placeholder="你的密码" required autoComplete="current-password" disabled={loading}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text transition-colors" aria-label={showPassword ? '隐藏密码' : '显示密码'}>
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <motion.button
              type="submit" disabled={loading}
              whileHover={loading ? {} : { scale: 1.02 }} whileTap={loading ? {} : { scale: 0.97 }}
              transition={snappySpring}
              className="group w-full py-3.5 brand-gradient disabled:opacity-60 disabled:cursor-not-allowed text-white font-black rounded-xl glow-primary flex items-center justify-center gap-2 tracking-wide"
            >
              <AnimatePresence mode="wait" initial={false}>
                {loading ? (
                  <motion.span key="loading" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="flex items-center gap-2">
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                    登录中...
                  </motion.span>
                ) : (
                  <motion.span key="idle" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="flex items-center gap-2">
                    进 入 网 盘
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1.5" />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            <div className="mt-5 flex items-center justify-center gap-3 text-[10px] text-text-muted/60 font-mono tracking-widest">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-border" />
              SAFE · FAST · PRIVATE
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-border" />
            </div>
          </div>
        </motion.form>

        <p className="text-center text-xs text-text-muted mt-6 tracking-wider">网盘由管理员管理 · 账号请勿外传</p>
      </div>
    </div>
  );
}
